import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getNavHint, getNavLabel } from '../../config/roleContent';
import { useToast } from '../../context/ToastContext';
import { useTheme } from '../../context/ThemeContext';
import { taskAPI, chatAPI, userAPI } from '../../api';

const NAV = [
  { to: '/dashboard', label: 'Dashboard', roles: ['CA', 'AM', 'CLNT'] },
  { to: '/users', label: 'Users', roles: ['CA', 'AM'] },
  { to: '/companies', label: 'Companies', roles: ['CA', 'AM', 'CLNT'] },
  { to: '/documents', label: 'Documents', roles: ['CA', 'AM', 'CLNT'] },
  { to: '/comments', label: 'Comments', roles: ['CA', 'AM', 'CLNT'] },
  { to: '/tasks', label: 'Assigned Tasks', roles: ['CA', 'AM'] },
  { to: '/chat', label: 'Direct Messages', roles: ['CA', 'AM', 'CLNT'] },
  { to: '/permissions', label: 'Permissions', roles: ['CA'] },
  { to: '/doc-categories', label: 'Doc Categories', roles: ['CA'] },
];

const ICONS = {
  Dashboard: (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 12h8V3H3zM13 21h8v-8h-8zM13 3h8v6h-8zM3 14h8v7H3z" />
    </svg>
  ),
  Users: (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="8" cy="8" r="3" />
      <path d="M2 20c0-3.3 2.7-6 6-6" />
      <circle cx="17" cy="8" r="3" />
      <path d="M14 14c3.3 0 6 2.7 6 6" />
    </svg>
  ),
  Companies: (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 21V3h10v18M13 7h8v14" />
      <path d="M7 7h2M7 11h2M7 15h2M17 11h2M17 15h2" />
    </svg>
  ),
  Documents: (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M7 3h7l5 5v13H7z" />
      <path d="M14 3v6h6" />
    </svg>
  ),
  Comments: (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15a4 4 0 0 1-4 4H7l-4 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
    </svg>
  ),
  'Assigned Tasks': (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
      <path d="M12 11H3" />
    </svg>
  ),
  'Direct Messages': (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 0 1-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  ),
  Permissions: (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="11" width="18" height="10" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
  'Doc Categories': (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 7h18M3 12h12M3 17h8" />
      <circle cx="19" cy="16" r="3" />
      <path d="M19 13v-2M19 19v2M22 16h2M16 16h-2" />
    </svg>
  ),
};

function BellIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

function ThemeIcon({ theme }) {
  if (theme === 'light') {
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
      </svg>
    );
  }

  if (theme === 'dark') {
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
      </svg>
    );
  }

  if (theme === 'forest') {
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3 6 12h4l-2 9 10-13h-4l2-5Z" />
      </svg>
    );
  }

  if (theme === 'rose') {
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 20s-6.5-4.35-8.5-8.1A4.84 4.84 0 0 1 12 6a4.84 4.84 0 0 1 8.5 5.9C18.5 15.65 12 20 12 20Z" />
      </svg>
    );
  }

  if (theme === 'ocean') {
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 14c2.5 0 2.5-2 5-2s2.5 2 5 2 2.5-2 5-2 2.5 2 3 2" />
        <path d="M3 18c2.5 0 2.5-2 5-2s2.5 2 5 2 2.5-2 5-2 2.5 2 3 2" />
        <path d="M4 10c2-4 5-6 8-6s6 2 8 6" />
      </svg>
    );
  }

  if (theme === 'graphite') {
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 4h12l2 5-8 11L4 9l2-5Z" />
        <path d="M9 4l3 16 3-16" />
      </svg>
    );
  }

  if (theme === 'modern_action') {
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M13 3 4 14h6l-1 7 9-11h-6l1-7Z" />
      </svg>
    );
  }

  if (theme === 'clean_productivity') {
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="4" width="16" height="16" rx="3" />
        <path d="M8 9h8M8 13h8M8 17h5" />
      </svg>
    );
  }

  if (theme === 'kpi_focus') {
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 19h16" />
        <path d="M7 16V9" />
        <path d="M12 16V5" />
        <path d="M17 16v-4" />
      </svg>
    );
  }

  if (theme === 'mono') {
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 3v18" />
        <path d="M12 3a9 9 0 0 1 0 18" fill="currentColor" stroke="none" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="M16 17l5-5-5-5" />
      <path d="M21 12H9" />
    </svg>
  );
}

function MenuIcon() {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" style={{ display: 'block' }}>
      <path d="M2.5 4h11" />
      <path d="M2.5 8h11" />
      <path d="M2.5 12h11" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}

function TourIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 6h16" />
      <path d="M4 12h10" />
      <path d="M4 18h7" />
      <path d="m17 15 3 3 4-8" />
    </svg>
  );
}

export default function Sidebar({ onStartTour, forceMobileOpen = false, tourActive = false }) {
  const { user, logout, updateUser } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, themeOptions, cycleTheme } = useTheme();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebar_collapsed') === '1');
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 980 : false));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showLogoutAlert, setShowLogoutAlert] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [taskCount, setTaskCount] = useState(0);
  const [hasTasks, setHasTasks] = useState(false);
  const [allTasksClosed, setAllTasksClosed] = useState(false);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const taskIdsRef = useRef(null);
  const notificationRef = useRef(null);

  useEffect(() => {
    if (!user?.id) return;
    userAPI.get(user.id).then(({ data }) => {
      if (data?.profile_photo && data.profile_photo !== user.profile_photo) {
        updateUser({ profile_photo: data.profile_photo });
      }
    }).catch(() => {});
  }, [user?.id]);

  useEffect(() => {
    if (!user || (user.role !== 'CA' && user.role !== 'AM')) return;

    const checkNotifications = async () => {
      try {
        const { data } = await taskAPI.getNotificationCount();
        const notifs = data.notifications || [];
        
         setTaskCount(data.count || 0);
         setHasTasks(data.has_tasks || false);
         setAllTasksClosed(data.all_closed || false);
        setNotifications(notifs.map(n => {
          let title = '';
          let desc = '';
          if (user.role === 'AM') {
            if (n.status === 'completed') {
              title = 'Task Approved';
              desc = 'CA approved the work. Task closed successfully.';
            } else if (n.status === 'pending_fees') {
              title = 'Task Approved — Fees Pending';
              desc = 'CA approved the work. Awaiting fee settlement.';
            } else if (n.is_transferred) {
              title = 'Task Assigned to You';
              desc = 'This task has been transferred to you.';
            } else if (n.is_sent_back) {
              title = 'Task Sent Back';
              desc = 'CA sends back. Please complete that.';
            } else {
              title = 'New Task Assigned';
              desc = 'A new task has been assigned to you.';
            }
          } else {
            if (n.status === 'pending' && n.created_by_am) {
              title = 'New Task Created';
              desc = `${n.creator_name} has created a new task.`;
            } else if (n.status === 'pending') {
              title = 'Task in Progress';
              desc = `${n.assigned_to_name} is currently working on a task.`;
            } else {
              title = 'Review Task';
              desc = `${n.assigned_to_name} has marked done. Please review and approve.`;
            }
          }
          return {
            id: `task-${n.id}`,
            title,
            desc,
            link: `/tasks/${n.id}`
          };
        }));
      } catch (err) { console.error("Notification sync failed", err); }
    };

    checkNotifications();
    const interval = setInterval(checkNotifications, 10000);
    return () => clearInterval(interval);
  }, [user?.id, user?.role, toast]);

  useEffect(() => {
    if (!user || !['CA', 'AM', 'CLNT'].includes(user.role)) return;

    const checkChatUnread = async () => {
      try {
        const { data } = await chatAPI.threads();
        const total = (Array.isArray(data) ? data : []).reduce((sum, t) => sum + (t.unread_count || 0), 0);
        setChatUnreadCount(total);
      } catch { /* silent */ }
    };

    checkChatUnread();
    const interval = setInterval(checkChatUnread, 10000);
    return () => clearInterval(interval);
  }, [user?.id, user?.role]);

  const handleMarkAllRead = async () => {
    try {
      await taskAPI.markAllTasksRead();
      setTaskCount(0);
      setHasTasks(true); // Keep the indicator showing status
      setNotifications([]);
    } catch (err) { toast.error("Failed to clear notifications"); }
  };

  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', collapsed ? '1' : '0');
  }, [collapsed]);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < 980;
      setIsMobile(mobile);
      if (!mobile) {
        setMobileOpen(false);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!isMobile) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = mobileOpen ? 'hidden' : previous;
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isMobile, mobileOpen]);

  useEffect(() => {
    if (isMobile && tourActive) {
      setMobileOpen(forceMobileOpen);
    }
  }, [forceMobileOpen, isMobile, tourActive]);

  useEffect(() => {
    setMobileOpen(false);
    setShowLogoutAlert(false);
    if (location.pathname === '/chat') setChatUnreadCount(0);
  }, [location.pathname, user?.role]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotifications]);

  // Inject blinking animations
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes blink-red {
        0% { opacity: 1; box-shadow: 0 0 8px rgba(255,0,0,0.6); }
        50% { opacity: 0.3; box-shadow: 0 0 2px rgba(255,0,0,0.2); }
        100% { opacity: 1; box-shadow: 0 0 8px rgba(255,0,0,0.6); }
      }
      @keyframes blink-green {
        0% { opacity: 1; box-shadow: 0 0 8px rgba(34,197,94,0.6); }
        50% { opacity: 0.3; box-shadow: 0 0 2px rgba(34,197,94,0.2); }
        100% { opacity: 1; box-shadow: 0 0 8px rgba(34,197,94,0.6); }
      }
      @keyframes tw-fadein {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .tw-notif-item:hover {
        background: var(--bg-elev) !important;
        border-color: var(--brand) !important;
      }
      .nav-indicator-dot {
        width: 8px; height: 8px; border-radius: 50%;
        background: #ef4444; margin-left: auto;
        box-shadow: 0 0 8px rgba(255,0,0,0.6);
        animation: blink-red 2s infinite ease-in-out;
      }
      .nav-indicator-dot-green {
        width: 8px; height: 8px; border-radius: 50%;
        background: #22c55e; margin-left: auto;
        box-shadow: 0 0 8px rgba(34,197,94,0.6);
        animation: blink-green 2s infinite ease-in-out;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/login');
    } finally {
      setLoggingOut(false);
      setShowLogoutAlert(false);
    }
  };

  const visible = useMemo(
    () => NAV.filter((item) => item.roles.includes(user?.role)),
    [user?.role]
  );
  const visibleNavItems = useMemo(
    () => visible.map((item) => ({ ...item, displayLabel: getNavLabel(item.label, user?.role) })),
    [visible, user?.role]
  );

  const nameParts = (user?.name || '').trim().split(/\s+/).filter(Boolean);
  const displayName = nameParts.length <= 1
    ? (nameParts[0] || 'My Account')
    : `${nameParts[0]} ${nameParts[nameParts.length - 1]}`;
  const initials = nameParts.length === 0 ? 'U'
    : nameParts.filter((_, i, a) => i === 0 || i === a.length - 1).map((p) => p[0].toUpperCase()).join('');

  const palette = {
    sidebarBg: 'var(--sidebar-bg)',
    cardBg: 'var(--sidebar-card)',
    cardBgStrong: 'var(--sidebar-card-strong)',
    line: 'var(--sidebar-line)',
    text: 'var(--sidebar-text)',
    muted: 'var(--sidebar-muted)',
    faint: 'var(--sidebar-faint)',
    accent: 'var(--sidebar-accent)',
    accentSoft: 'var(--sidebar-accent-soft)',
    accentGlow: 'var(--sidebar-accent-glow)',
    avatarBg: 'var(--sidebar-avatar)',
    logout: 'var(--sidebar-logout)',
  };

  const closeMobileMenu = () => {
    if (isMobile) setMobileOpen(false);
  };

  const currentThemeLabel = themeOptions.find((option) => option.id === theme)?.label || 'Theme';

  const logoutAlert = showLogoutAlert ? (
    <div style={styles.alertOverlay} onClick={() => !loggingOut && setShowLogoutAlert(false)}>
      <div
        style={{
          ...styles.alertCard,
          background: 'var(--bg-surface)',
          border: `1px solid var(--border)`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            ...styles.alertIcon,
            background: palette.cardBgStrong,
            color: palette.logout,
          }}
        >
          <LogoutIcon />
        </div>
        <div style={{ ...styles.alertTitle, color: 'var(--text-primary)' }}>Sign out now?</div>
        <div style={{ ...styles.alertText, color: 'var(--text-muted)' }}>
          You will be signed out of this workspace and returned to the login screen.
        </div>
        <div style={styles.alertActions}>
          <button
            type="button"
            onClick={() => setShowLogoutAlert(false)}
            style={{
              ...styles.alertBtn,
              border: '1px solid var(--border)',
              background: 'var(--bg-elev)',
              color: 'var(--text-primary)',
            }}
            disabled={loggingOut}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleLogout}
            style={{
              ...styles.alertBtn,
              background: 'var(--brand)',
              color: 'var(--brand-contrast)',
            }}
            disabled={loggingOut}
          >
            {loggingOut ? 'Signing out...' : 'Sign Out'}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  const sidebarContent = (
    <div style={styles.inner}>
      <div style={styles.top}>
        {collapsed && !isMobile ? (
          <div
            style={{
              ...styles.brandShell,
              ...styles.brandShellCollapsed,
              border: `1px solid ${palette.line}`,
              background: palette.cardBg,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 2, flexShrink: 0 }}>
              <div style={{ ...styles.avatar, background: palette.avatarBg, color: '#101926' }}>
                {initials || 'U'}
                {user?.profile_photo && (
                  <img src={user.profile_photo} alt={displayName} style={styles.avatarImage} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                )}
              </div>
            </div>
            <button
              onClick={() => setCollapsed((value) => !value)}
              style={{
                ...styles.collapseBtn,
                ...styles.collapseBtnStacked,
                border: `1px solid ${palette.line}`,
                background: palette.cardBgStrong,
                color: palette.text,
              }}
              title="Expand sidebar"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: 'rotate(180deg)', transition: 'transform 0.18s ease' }}>
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
          </div>
        ) : (
          <div
            style={{
              ...styles.brandShell,
              border: `1px solid ${palette.line}`,
              background: palette.cardBg,
            }}
          >
            <div style={{ ...styles.profileCard, flex: 1, minWidth: 0 }}>
              <div style={{ ...styles.avatar, background: palette.avatarBg, color: '#101926' }}>
                {initials || 'U'}
                {user?.profile_photo && (
                  <img src={user.profile_photo} alt={displayName} style={styles.avatarImage} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                )}
              </div>
              <div style={styles.profileMeta}>
                <div style={{ ...styles.profileName, color: palette.text }}>{displayName}</div>
                <div style={{ ...styles.profileHint, color: palette.faint }}>CA CRM</div>
              </div>
            </div>
            {isMobile ? (
              <button
                onClick={() => setMobileOpen(false)}
                style={{ ...styles.collapseBtn, border: `1px solid ${palette.line}`, background: palette.cardBgStrong, color: palette.text }}
                title="Close sidebar"
              >
                <CloseIcon />
              </button>
            ) : (
              <button
                onClick={() => setCollapsed((value) => !value)}
                style={{ ...styles.collapseBtn, border: `1px solid ${palette.line}`, background: palette.cardBgStrong, color: palette.text }}
                title="Collapse sidebar"
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" style={{ transition: 'transform 0.18s ease' }}>
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
            )}
          </div>
        )}

        {(!collapsed || isMobile) && (
          <div style={{ ...styles.sectionLabel, color: palette.faint }}>
            Workspace
          </div>
        )}

        <nav style={styles.nav}>
          {visibleNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              title={item.displayLabel}
              onClick={closeMobileMenu}
              data-tour={`nav-${item.label.toLowerCase()}`}
              style={({ isActive }) => ({
                ...styles.link,
                ...(collapsed && !isMobile ? styles.linkCollapsed : null),
                color: isActive ? palette.text : palette.muted,
                border: `1px solid ${isActive ? palette.accentSoft : 'transparent'}`,
                background: isActive ? `linear-gradient(135deg, ${palette.accentSoft} 0%, rgba(255,255,255,0.02) 100%)` : 'transparent',
                boxShadow: isActive ? `0 16px 30px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.05)` : 'none',
              })}
            >
              <span
                style={{
                  ...styles.iconWrap,
                  background: collapsed && !isMobile ? palette.cardBgStrong : palette.cardBg,
                  color: 'currentColor',
                  border: `1px solid ${palette.line}`,
                }}
              >
                {ICONS[item.label]}
              </span>
              {(!collapsed || isMobile) && (
                <span style={styles.linkContent}>
                  <span style={styles.linkLabel}>{item.displayLabel}</span>
                  <span style={{ ...styles.linkHint, color: palette.faint }}>{getNavHint(item.label, user?.role)}</span>
                </span>
              )}
              {item.label === 'Assigned Tasks' && hasTasks && (
                <div
                  className={allTasksClosed ? "nav-indicator-dot-green" : "nav-indicator-dot"}
                  style={collapsed && !isMobile ? { position: 'absolute', top: 12, right: 12, margin: 0 } : {}}
                />
              )}
              {item.label === 'Direct Messages' && chatUnreadCount > 0 && (
                <span style={{
                  minWidth: 18, height: 18, borderRadius: 999,
                  background: '#ef4444', color: '#fff',
                  fontSize: 10, fontWeight: 800,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '0 5px', flexShrink: 0,
                  ...(collapsed && !isMobile ? { position: 'absolute', top: 10, right: 10 } : {}),
                }}>
                  {chatUnreadCount > 99 ? '99+' : chatUnreadCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
      </div>

      <div style={{ ...styles.bottom, borderTop: `1px solid ${palette.line}` }}>
        <div style={{ ...styles.actionStack, ...(collapsed && !isMobile ? styles.actionStackCollapsed : null) }}>
          <NavLink
            to="/profile"
            onClick={closeMobileMenu}
            data-tour="profile-settings"
            style={({ isActive }) => ({
              ...styles.actionBtn,
              border: `1px solid ${palette.line}`,
              background: isActive ? palette.accentSoft : palette.cardBg,
              color: palette.text,
              textDecoration: 'none',
            })}
            title="Profile settings"
          >
            <span style={styles.actionIcon}>
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="8" r="4" />
                <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
              </svg>
            </span>
            {(!collapsed || isMobile) && <span>Profile settings</span>}
          </NavLink>
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            data-tour="notifications"
            style={{ ...styles.actionBtn, border: `1px solid ${palette.line}`, background: palette.cardBg, color: palette.text, position: 'relative' }}
            title="Notifications"
          >
            <span style={styles.actionIcon}>
              <BellIcon />
              {taskCount > 0 && (
                <span style={{
                  position: 'absolute', top: 8, right: collapsed && !isMobile ? 8 : 'auto', left: collapsed && !isMobile ? 'auto' : 28,
                  width: 16, height: 16, borderRadius: '50%', background: '#ef4444', color: '#fff',
                  fontSize: 9, fontWeight: 800, display: 'grid', placeItems: 'center',
                  boxShadow: `0 0 0 2px ${palette.sidebarBg}`,
                  animation: 'blink-red 2s infinite ease-in-out'
                }}>
                  {taskCount}
                </span>
              )}
            </span>
            {(!collapsed || isMobile) && <span>Notifications</span>}
          </button>
          <button
            onClick={() => setShowLogoutAlert(true)}
            data-tour="logout-btn"
            style={{ ...styles.actionBtn, border: `1px solid ${palette.line}`, background: palette.cardBg, color: palette.logout }}
            title="Sign out"
          >
            <span style={styles.actionIcon}><LogoutIcon /></span>
            {(!collapsed || isMobile) && <span>Sign Out</span>}
          </button>
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        <button
          onClick={() => setMobileOpen(true)}
          style={{
            ...styles.mobileToggle,
            background: theme === 'dark' ? palette.avatarBg : palette.sidebarBg,
            color: palette.text,
            border: `1px solid ${palette.line}`,
          }}
          title="Open sidebar"
        >
          <span style={styles.mobileToggleIcon}>
            <MenuIcon />
          </span>
        </button>

        {mobileOpen && (
          <>
            <div style={styles.mobileBackdrop} onClick={() => setMobileOpen(false)} />
            <aside
              style={{
                ...styles.sidebar,
                ...styles.mobileSidebar,
                width: 'min(86vw, 300px)',
                maxWidth: 'calc(100vw - 20px)',
                background: palette.sidebarBg,
                borderRight: `1px solid ${palette.line}`,
              }}
            >
              {sidebarContent}
            </aside>
          </>
        )}
        {logoutAlert}
        {showNotifications && (
          <div ref={notificationRef} style={{
            ...styles.notificationPanel,
            left: 12,
            bottom: 80,
            width: 'calc(100% - 24px)',
          }}>
            <div style={styles.panelHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={styles.panelHeaderTitle}>Notifications</div>
                {taskCount > 0 && <button onClick={handleMarkAllRead} style={styles.clearAllBtn}>Clear All</button>}
              </div>
              <button onClick={() => setShowNotifications(false)} style={styles.panelCloseBtn}>×</button>
            </div>
            <div style={styles.panelList}>
              {notifications.length === 0 ? (
                <div style={styles.emptyPanel}>
                  <BellIcon />
                  <p>No new notifications</p>
                </div>
              ) : (
                notifications.map((n) => (
                  <div 
                    key={n.id} 
                    className="tw-notif-item"
                    onClick={async () => { 
                      const tid = n.id.replace('task-', '');
                      taskAPI.markTaskRead(tid).catch(() => {});
                      navigate(n.link); 
                      setShowNotifications(false); 
                      setMobileOpen(false);
                    }} 
                    style={styles.notifItem}
                  >
                    <div style={styles.notifHeader}>
                      <span style={styles.notifTitle}>{n.title}</span>
                    </div>
                    <div style={styles.notifDesc}>{n.desc}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <aside
        style={{
          ...styles.sidebar,
          width: collapsed ? 94 : 292,
          background: palette.sidebarBg,
          borderRight: `1px solid ${palette.line}`,
        }}
      >
        {sidebarContent}
      </aside>
      {logoutAlert}
      {showNotifications && (
        <div ref={notificationRef} style={{
          ...styles.notificationPanel,
          left: collapsed ? 84 : 292,
          bottom: 16,
        }}>
          <div style={styles.panelHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={styles.panelHeaderTitle}>Notifications</div>
              {taskCount > 0 && <button onClick={handleMarkAllRead} style={styles.clearAllBtn}>Clear All</button>}
            </div>
            <button onClick={() => setShowNotifications(false)} style={styles.panelCloseBtn}>×</button>
          </div>
          <div style={styles.panelList}>
            {notifications.length === 0 ? (
              <div style={styles.emptyPanel}>
                <BellIcon />
                <p>No new notifications</p>
              </div>
            ) : (
              notifications.map((n) => (
                <div 
                  key={n.id} 
                  className="tw-notif-item"
                    onClick={async () => { 
                      const tid = n.id.replace('task-', '');
                      taskAPI.markTaskRead(tid).catch(() => {});
                    navigate(n.link); 
                    setShowNotifications(false); 
                  }} 
                  style={styles.notifItem}
                >
                  <div style={styles.notifHeader}>
                    <span style={styles.notifTitle}>{n.title}</span>
                  </div>
                  <div style={styles.notifDesc}>{n.desc}</div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </>
  );
}

const styles = {
  sidebar: {
    height: '100dvh',
    maxHeight: '100dvh',
    position: 'sticky',
    top: 0,
    flexShrink: 0,
    transition: 'width 0.22s ease',
    overflow: 'hidden',
    boxShadow: 'inset -1px 0 0 rgba(255,255,255,0.03)',
    zIndex: 40,
  },
  mobileSidebar: {
    position: 'fixed',
    left: 0,
    top: 0,
    boxShadow: '0 24px 60px rgba(0,0,0,0.28)',
  },
  mobileBackdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(7,12,18,0.48)',
    zIndex: 30,
  },
  mobileToggle: {
    position: 'fixed',
    left: 12,
    top: 10,
    zIndex: 20,
    width: 44,
    height: 44,
    padding: 0,
    borderRadius: 14,
    cursor: 'pointer',
    lineHeight: 0,
    appearance: 'none',
    WebkitAppearance: 'none',
    boxShadow: '0 14px 32px rgba(0,0,0,0.22)',
  },
  mobileToggleIcon: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
  },
  inner: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: 16,
    gap: 18,
  },
  top: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  brandShell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: 14,
    borderRadius: 24,
    backdropFilter: 'blur(8px)',
  },
  brandShellCollapsed: {
    padding: '14px 10px',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    gap: 12,
  },
  brandMain: {
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  brandMark: {
    width: 48,
    height: 48,
    borderRadius: 16,
    display: 'grid',
    placeItems: 'center',
    fontSize: 14,
    fontWeight: 800,
    letterSpacing: 0.9,
    flexShrink: 0,
  },
  brandCopy: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  brandEyebrow: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.9,
    textTransform: 'uppercase',
  },
  brandTitle: {
    fontSize: 18,
    fontWeight: 800,
    letterSpacing: 0.2,
    lineHeight: 1.1,
  },
  brandSub: {
    fontSize: 12,
    fontWeight: 500,
  },
  collapseBtn: {
    width: 34,
    height: 34,
    borderRadius: 12,
    padding: 0,
    display: 'grid',
    placeItems: 'center',
    cursor: 'pointer',
    flexShrink: 0,
  },
  collapseBtnStacked: {
    width: 32,
    height: 32,
    borderRadius: 10,
  },
  sectionLabel: {
    padding: '2px 4px 0',
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  nav: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    overflowY: 'auto',
    paddingRight: 2,
  },
  link: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minHeight: 62,
    padding: '10px 12px',
    borderRadius: 20,
    textDecoration: 'none',
    transition: 'background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease',
  },
  linkCollapsed: {
    justifyContent: 'center',
    minHeight: 58,
    padding: 8,
  },
  iconWrap: {
    width: 40,
    height: 40,
    borderRadius: 14,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  linkContent: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  linkLabel: {
    fontSize: 14,
    fontWeight: 700,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  linkHint: {
    fontSize: 11,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  bottom: {
    paddingTop: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  profileCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    borderRadius: 22,
    textDecoration: 'none',
    transition: 'background 0.18s ease, border-color 0.18s ease',
  },
  profileCardCollapsed: {
    justifyContent: 'center',
    padding: 10,
  },
  avatar: {
    position: 'relative',
    width: 44,
    height: 44,
    borderRadius: 16,
    display: 'grid',
    placeItems: 'center',
    fontSize: 14,
    fontWeight: 800,
    flexShrink: 0,
    overflow: 'hidden',
  },
  avatarImage: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  profileMeta: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  profileName: {
    fontSize: 13,
    fontWeight: 700,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  profileRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
  },
  rolePill: {
    padding: '3px 8px',
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 0.5,
    flexShrink: 0,
  },
  profileHint: {
    fontSize: 11,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  actionStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  actionStackCollapsed: {
    display: 'grid',
    gridTemplateColumns: '1fr',
  },
  actionBtn: {
    minHeight: 44,
    borderRadius: 14,
    padding: '10px 12px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
  },
  actionIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  alertOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(8, 12, 18, 0.28)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    zIndex: 120,
  },
  alertCard: {
    width: '100%',
    maxWidth: 360,
    borderRadius: 20,
    padding: '22px 20px 18px',
    boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: 10,
  },
  alertIcon: {
    width: 44,
    height: 44,
    borderRadius: 14,
    display: 'grid',
    placeItems: 'center',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.18)',
  },
  alertTitle: {
    fontSize: 20,
    fontWeight: 800,
  },
  alertText: {
    fontSize: 13,
    lineHeight: 1.55,
    maxWidth: 280,
  },
  alertActions: {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
    marginTop: 8,
  },
  alertBtn: {
    minHeight: 42,
    borderRadius: 12,
    padding: '0 14px',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
  },
  notificationPanel: {
    position: 'fixed',
    width: 300,
    maxHeight: 420,
    background: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    borderRadius: 20,
    boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    animation: 'tw-fadein 0.2s ease both',
  },
  panelHeader: {
    padding: '16px 18px',
    borderBottom: '1px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: 'var(--bg-elev)',
  },
  panelHeaderTitle: {
    fontSize: 14,
    fontWeight: 800,
    color: 'var(--text-primary)',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  panelCloseBtn: {
    border: 'none',
    background: 'transparent',
    fontSize: 20,
    color: 'var(--text-soft)',
    cursor: 'pointer',
    lineHeight: 1,
  },
  panelList: { flex: 1, overflowY: 'auto', padding: '8px' },
  emptyPanel: {
    padding: '40px 20px',
    textAlign: 'center',
    color: 'var(--text-soft)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
  },
  notifItem: {
    padding: '12px 14px',
    borderRadius: 14,
    cursor: 'pointer',
    transition: 'background 0.2s ease',
    borderBottom: '1px solid var(--border)',
  },
  notifHeader: { marginBottom: 4 },
  notifTitle: { fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' },
  notifDesc: { fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.4 },
  clearAllBtn: {
    fontSize: 10,
    fontWeight: 800,
    color: 'var(--brand)',
    background: 'transparent',
    border: 'none',
    textTransform: 'uppercase',
    cursor: 'pointer',
    padding: '4px 0',
  },
};
